import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { Modal, ModalHeader } from '@app/components';
import { wrapBoldTranslationChunks } from '@app/utils';
import { sharedMessages } from '@app/translations';

import { ZoomMap } from './ZoomMap';

import './StackMapModal.scss';

export interface StackMapDto {
  mapurl: string;
  callNo: string;
  directions: string;
  ranges: {
    rangename: string;
  }[];
  printMapurl: string;
}

interface StackMapModalProps {
  isOpen: boolean;
  stackMapData: StackMapDto;
  onModalClose: () => void;
}

const cnBem = createBemBlockBuilder(['stackMapModal']);

export const StackMapModal: FC<StackMapModalProps> = ({
  isOpen,
  stackMapData: {
    mapurl,
    directions,
    callNo,
    ranges = [],
    printMapurl: printMapUrl,
  },
  onModalClose,
}) => {
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const { $t } = useIntl();

  const itemDirections = [
    $t(
      {
        id: 'stackMap.direction',
        defaultMessage: '<b>Direction:</b> {direction}',
      },
      {
        direction: directions,
        b: wrapBoldTranslationChunks,
      }
    ),
    $t(
      {
        id: 'stackMap.shelvingRow',
        defaultMessage: '<b>Go to the shelving row labeled:</b> {row}',
      },
      {
        row: ranges[0]?.rangename,
        b: wrapBoldTranslationChunks,
      }
    ),
    $t(
      {
        id: 'stackMap.lookForCallNumber',
        defaultMessage:
          '<b>Look for the item with this call number:</b> {callNo}',
      },
      {
        callNo,
        b: wrapBoldTranslationChunks,
      }
    ),
  ];

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={$t(sharedMessages.map)}
      className={cnBem()}
      onClose={onModalClose}
      onAfterClose={() => setIsMapLoaded(false)}
    >
      <ModalHeader
        title={$t(sharedMessages.map)}
        titleLevel={2}
        onClose={onModalClose}
      />
      <Modal.Body>
        <ZoomMap
          imgSrc={mapurl}
          isImageLoaded={isMapLoaded}
          printMapUrl={printMapUrl}
          onImageLoad={() => setIsMapLoaded(true)}
        />
        <div
          className={cnBem('__directionsWrapper')}
          data-auto="stackMapDirections"
        >
          {itemDirections.map((direction, index) => (
            <div className={cnBem('__direction')} key={index}>
              {direction}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button styleType="text" onClick={onModalClose}>
          {$t(sharedMessages.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
